import { ApexOptions } from 'apexcharts'
import BaseOptionChart from 'common/components/charts/BaseOptionChart'
import { createArrayOfNumber } from 'common/utils/array'
import { dynamicTickAmount, integerFormatter } from 'common/utils/chart'
import { formatNumber } from 'common/utils/formatNumber'
import merge from 'lodash/merge'
import { useGetXTitleLineChart } from 'modules/pond/api/selector'
import ReactApexChart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'

import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import './style.css'


interface LineChartProps {
  yData: ApexAxisChartSeries
  isLoading: boolean
  dataLength: number
  options?: ApexOptions
  min?: number
  max?: number
  yTitle?: string
}

const DashboardLineChart = ({
  yData,
  isLoading,
  dataLength,
  options,
  min,
  max,
}: LineChartProps) => {
  const { t } = useTranslation(['pond', 'food'])
  const theme = useTheme()
  const xTitle = useGetXTitleLineChart()

  const baseYaxisOption = {
    title: {
      text: t('food:food.amount.kg'),
    },
    labels: {
      formatter: formatNumber,
    },
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
  }

  const chartOptions: ApexOptions = merge(BaseOptionChart(), {
    xaxis: {
      type: 'numeric',
      title: { text: xTitle },
      tickAmount: dynamicTickAmount(dataLength),
      labels: { formatter: integerFormatter },
    },
    yaxis: baseYaxisOption,
    legend: { position: 'bottom', horizontalAlign: 'start' },
    responsive: [
      {
        breakpoint: theme.breakpoints.values.sm,
        options: {
          legend: { position: 'bottom' },
          yaxis: {
            title: { text: undefined },
            labels: { ...baseYaxisOption.labels, offsetX: -10 },
          },
          xaxis: { title: { offsetY: -20 } },
        },
      },
    ],
    ...options,
  })

  if (isLoading)
    return (
      <Stack spacing={3} p={2}>
        <Stack direction="row" justifyContent="flex-start" spacing={1}>
          {createArrayOfNumber(3).map((i) => (
            <Skeleton width={60} key={i} />
          ))}
        </Stack>
        {createArrayOfNumber(6).map((i) => (
          <Stack direction="row" key={i} spacing={1}>
            <Skeleton width={40} />
            <Skeleton width="100%" />
          </Stack>
        ))}
        <Stack direction="row" spacing={2}>
          {createArrayOfNumber(6).map((i) => (
            <Skeleton key={i} width="100%" />
          ))}
        </Stack>
      </Stack>
    )

  return (
    <ReactApexChart
      className="dashboard-line-chart"
      type="line"
      series={yData}
      options={chartOptions}
      height={420}
    />
  )
}

export default DashboardLineChart
